import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend, Tooltip } from 'recharts'

interface RadarChartData {
  domain: string
  score: number
  fullMark: number
  benchmark?: number
}

interface BenchmarkConfig {
  key: string
  name: string
  color: string
}

interface RadarChartProps {
  data: RadarChartData[]
  title?: string
  description?: string
  benchmarks?: BenchmarkConfig[]
}

export function RadarChartComponent({
  data,
  title = "IT Maturity Radar",
  description,
  benchmarks = [],
}: RadarChartProps) {
  // Validate and sanitize data
  const validData = data?.filter(item =>
    item &&
    typeof item.domain === 'string' &&
    typeof item.score === 'number' &&
    !isNaN(item.score) &&
    item.score >= 0
  ) || []

  // If no valid data, show placeholder
  if (validData.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {description && (
            <p className="text-sm text-gray-600 mt-1">{description}</p>
          )}
        </div>

        <div className="h-80 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <p>No data available for radar chart</p>
            <p className="text-sm mt-1">Complete the assessment to see results</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {description && (
          <p className="text-sm text-gray-600 mt-1">{description}</p>
        )}
      </div>

      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={validData}>
            <PolarGrid />
            <PolarAngleAxis dataKey="domain" tick={{ fontSize: 11 }} />
            <PolarRadiusAxis
              angle={90}
              domain={[0, 5]}
              tickCount={6}
              tick={{ fontSize: 12 }}
              tickFormatter={(v: number) => `L${v}`}
            />
            {benchmarks.map((bm) => (
              <Radar
                key={bm.key}
                name={bm.name}
                dataKey={bm.key}
                stroke={bm.color}
                fill={bm.color}
                fillOpacity={0.05}
                strokeWidth={2}
                strokeDasharray="6 3"
              />
            ))}
            <Radar
              name="Your Score"
              dataKey="score"
              stroke="#3b82f6"
              fill="#3b82f6"
              fillOpacity={0.3}
              strokeWidth={2}
            />
            {benchmarks.length > 0 && <Legend />}
            <Tooltip formatter={(value: number) => `Level ${value}`} />
          </RadarChart>
        </ResponsiveContainer>

      </div>

      <div className="mt-4 text-xs text-gray-500">
        <p>Scale: Level 1 → Level 5</p>
      </div>
    </div>
  )
}
